<script>
  import { onMount, onDestroy } from 'svelte';
  import { t } from '../i18n/index.js';
  import { showScreen } from '../stores/screen.js';
  import { session, connectionStatus, deviceName, updateConnectionStatus, isRecovering, subscription } from '../stores/session.js';
  import { scannedPages, addPage, savePendingCapture, clearPendingCapture } from '../stores/pages.js';
  import { scanMode, defaultFilter } from '../stores/settings.js';

  // Camera and detection modules
  import { initCamera, stopCamera, capturePhoto, captureHighResPhoto } from '../camera.js';
  import { loadDocAlignerModel, isDocAlignerReady, warmupDocAligner, scheduleDocAlignerRelease, cancelDocAlignerRelease } from '../docaligner-inference.js';
  import { loadOpenCV, isOpenCVReady } from '../opencv-loader.js';
  import { detectCorners, applyPerspectiveCorrection } from '../document-scanner.js';
  import { createRealtimeDetector } from '../realtime-detector.js';
  import { StabilityState } from '../stability-tracker.js';
  import { captureTransition } from '../capture-transition.js';
  import { applyFilter } from '../image-processor.js';
  import { getImageSize, scaleCorners, loadImage } from '../utils/image-utils.js';
  import { startSessionHealthCheck, stopSessionHealthCheck } from '../lib/session-health.js';

  // Components
  import ConnectionStatus from '../components/common/ConnectionStatus.svelte';
  import ModeToggle from '../components/camera/ModeToggle.svelte';
  import CaptureButton from '../components/camera/CaptureButton.svelte';
  import RealtimeOverlay from '../components/camera/RealtimeOverlay.svelte';

  let { onModeSwitch, onDisconnected } = $props();

  // Element refs
  let videoElement;
  let canvasElement;
  let overlayCanvas = $state(null);

  // State
  let cameraReady = $state(false);
  let capturing = $state(false);
  let realtimeDetector = $state(null);
  let detectionState = $state(StabilityState.NOT_DETECTED);
  let queueCount = $state(0);
  let showUpgradeToast = $state(false);

  // Computed
  let isDocumentMode = $derived($scanMode === 'document');
  let isFreeUser = $derived($subscription.loaded && $subscription.dailyUploadLimit > 0);
  let remaining = $derived($subscription.dailyUploadLimit - $subscription.dailyUploadsUsed);

  function showUpgradeHint() {
    showUpgradeToast = true;
    setTimeout(() => { showUpgradeToast = false; }, 3000);
  }

  function handleVisibilityChange() {
    if (document.hidden) {
      // 백그라운드: 카메라 하드웨어(ISP) 중단으로 발열/배터리 절약
      const tracks = videoElement?.srcObject?.getVideoTracks();
      if (tracks) tracks.forEach(t => t.enabled = false);
      stopRealtimeDetection();
    } else {
      // 포그라운드 복귀: 카메라 즉시 재개
      const tracks = videoElement?.srcObject?.getVideoTracks();
      if (tracks) tracks.forEach(t => t.enabled = true);
      if ($scanMode === 'document') {
        startRealtimeDetection();
      }
    }
  }

  onMount(async () => {
    // 카메라 복귀 시 ONNX 세션 유예 해제 취소
    cancelDocAlignerRelease();

    try {
      await initCamera(videoElement, canvasElement);
      cameraReady = true;
      adjustOverlay();

      // 백그라운드 전환 시 카메라 중단 (발열/배터리 절약)
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Start session health check
      startSessionHealthCheck(
        (reason) => onDisconnected?.(reason),
        (isOnline, name) => {
          updateConnectionStatus(isOnline ? 'connected' : 'disconnected', name);
        }
      );

      // Start realtime detection in document mode
      if ($scanMode === 'document') {
        await startRealtimeDetection();
      }
    } catch (error) {
      console.error('Camera init failed:', error);
      if (error.message.includes('permission') || error.message.includes('denied')) {
        showScreen('cameraPermission');
      } else {
        showScreen('error');
      }
    }
  });

  onDestroy(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    stopCamera();
    // Note: Don't stop session health check here - it should persist across screens
    // stopSessionHealthCheck() is only called on explicit session end (settings reset, etc.)
    stopRealtimeDetection();
    // 30초 후 ONNX 세션 해제 (Camera→Thumbnails→Camera 왕복 시 재로드 방지)
    scheduleDocAlignerRelease();
  });

  function adjustOverlay() {
    if (!videoElement || !videoElement.videoWidth) return;

    // Adjust overlay to match video letterbox
    const containerWidth = videoElement.clientWidth;
    const containerHeight = videoElement.clientHeight;
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    const containerRatio = containerWidth / containerHeight;
    const videoRatio = videoWidth / videoHeight;

    let renderedWidth, renderedHeight, offsetX, offsetY;

    if (videoRatio > containerRatio) {
      renderedWidth = containerWidth;
      renderedHeight = containerWidth / videoRatio;
      offsetX = 0;
      offsetY = (containerHeight - renderedHeight) / 2;
    } else {
      renderedHeight = containerHeight;
      renderedWidth = containerHeight * videoRatio;
      offsetX = (containerWidth - renderedWidth) / 2;
      offsetY = 0;
    }

    if (overlayCanvas) {
      overlayCanvas.style.top = `${offsetY}px`;
      overlayCanvas.style.left = `${offsetX}px`;
      overlayCanvas.style.width = `${renderedWidth}px`;
      overlayCanvas.style.height = `${renderedHeight}px`;
    }
  }

  async function startRealtimeDetection() {
    stopRealtimeDetection();

    if (!isDocAlignerReady()) {
      try {
        await loadDocAlignerModel();
        await warmupDocAligner();
      } catch (e) {
        console.warn('DocAligner load failed:', e);
        return;
      }
    }

    // overlayCanvas가 DOM에 바인딩될 때까지 대기 (조건부 렌더링 race condition 방어)
    if (!overlayCanvas) {
      await new Promise(r => requestAnimationFrame(r));
    }
    if (!overlayCanvas) return;

    realtimeDetector = createRealtimeDetector(videoElement, overlayCanvas);

    realtimeDetector.onAutoCapture = async (corners, confidence) => {
      await handleAutoCapture(corners, confidence);
    };

    realtimeDetector.onStateChange = (state) => {
      detectionState = state;
    };

    realtimeDetector.onError = (error) => {
      console.error('Realtime detection error:', error);
    };

    await realtimeDetector.start();
  }

  function stopRealtimeDetection() {
    if (realtimeDetector) {
      realtimeDetector.destroy();
      realtimeDetector = null;
    }
  }

  async function handleAutoCapture(corners, confidence) {
    if (capturing) return;
    capturing = true;

    try {
      const videoWidth = videoElement.videoWidth;
      const videoHeight = videoElement.videoHeight;

      // 고해상도 캡처 먼저 (ImageCapture는 트랙이 활성 상태여야 함)
      const photo = await captureHighResPhoto();

      // 캡처 후 프리즈 (유저에게 코너 오버레이 보여줌)
      captureTransition.freezeWithCorners(videoElement, corners, overlayCanvas);

      const capturedSize = await getImageSize(photo);
      const scaledCorners = scaleCorners(corners, { width: videoWidth, height: videoHeight }, capturedSize);

      // Brief pause to show detected corners (reduced from 300ms)
      await new Promise(r => setTimeout(r, 150));

      // Start expand animation but don't hide yet
      captureTransition.expandToFullscreen(() => {});

      // Process document while animation is running
      await processDocumentCapture(photo, scaledCorners);

      // Hide transition after screen changed
      captureTransition.hide();
    } catch (error) {
      console.error('Auto capture failed:', error);
      captureTransition.hide();
    } finally {
      capturing = false;
      if ($scanMode === 'document') {
        startRealtimeDetection();
      }
    }
  }

  async function handleManualCapture() {
    if (capturing) return;
    capturing = true;

    try {
      if (realtimeDetector && $scanMode === 'document') {
        realtimeDetector.stop();
      }

      // Visual feedback
      try { navigator.vibrate?.(30); } catch {}

      await new Promise(r => setTimeout(r, $scanMode === 'document' ? 150 : 400));

      const photo = await captureHighResPhoto();
      showCaptureFlash();

      await new Promise(r => setTimeout(r, 150));

      if ($scanMode === 'document') {
        // Detect corners and process
        let corners;
        try {
          corners = await detectCorners(photo);
        } catch (e) {
          // Fallback to default corners
          const img = await loadImage(photo);
          const inset = 0.1;
          corners = {
            tl: { x: img.width * inset, y: img.height * inset },
            tr: { x: img.width * (1 - inset), y: img.height * inset },
            br: { x: img.width * (1 - inset), y: img.height * (1 - inset) },
            bl: { x: img.width * inset, y: img.height * (1 - inset) }
          };
        }
        await processDocumentCapture(photo, corners);
      } else {
        // Simple mode: add directly
        addPage({ fullImage: photo, thumbnail: photo, filter: null });
        showScreen('thumbnails');
      }
    } catch (error) {
      console.error('Capture failed:', error);
    } finally {
      capturing = false;
      if ($scanMode === 'document') {
        startRealtimeDetection();
      }
    }
  }

  async function processDocumentCapture(photo, corners) {
    try {
      // Apply perspective correction
      if (!isOpenCVReady()) {
        await loadOpenCV(() => {});
      }

      // Let applyPerspectiveCorrection calculate output size from corners (preserves document ratio)
      const corrected = await applyPerspectiveCorrection(photo, corners);

      // Apply default filter (gray for document mode)
      const processed = await applyFilter(corrected, $defaultFilter || 'gray');

      addPage({
        rawImage: photo,        // Original camera capture for corner re-adjustment
        fullImage: corrected,   // Perspective corrected
        thumbnail: processed,   // Filtered version
        filter: $defaultFilter || 'gray',
        corners
      });

      showScreen('thumbnails');
    } catch (error) {
      console.error('Document processing failed:', error);
      // Fallback: add raw image
      addPage({ rawImage: photo, fullImage: photo, thumbnail: photo, filter: null, corners });
      showScreen('thumbnails');
    }
  }

  function showCaptureFlash() {
    const flash = document.getElementById('capture-flash');
    if (flash) {
      flash.classList.remove('hidden');
      flash.classList.add('capture-flash');
      flash.addEventListener('animationend', () => {
        flash.classList.add('hidden');
        flash.classList.remove('capture-flash');
      }, { once: true });
    }
  }

  function handleModeChange(newMode) {
    if (newMode === $scanMode) return;

    onModeSwitch?.(newMode);

    // Update detection
    if (newMode === 'document') {
      startRealtimeDetection();
    } else {
      stopRealtimeDetection();
    }
  }

  function openSettings() {
    stopCamera();
    showScreen('settings');
  }
</script>

<div class="screen flex flex-col h-full">
  <!-- Status bar -->
  <div class="status-bar absolute top-0 left-0 right-0 z-20 px-4 py-2 flex items-center justify-between">
    <div class="flex items-center gap-2">
      <ConnectionStatus status={$connectionStatus} deviceName={$deviceName} />
    </div>
    <button
      onclick={openSettings}
      class="p-2 rounded-full bg-black/50 hover:bg-black/70"
      aria-label="Settings"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    </button>
  </div>

  <!-- Queue status -->
  {#if queueCount > 0}
    <div class="queue-status absolute top-12 left-4 right-4 z-20 px-4 py-2 rounded-lg flex items-center justify-center gap-2">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span class="font-medium">{t('camera.queuePending', { count: queueCount })}</span>
    </div>
  {/if}

  <!-- Camera view -->
  <main class="flex-1 relative bg-black">
    <video
      bind:this={videoElement}
      class="camera-video absolute inset-0 w-full h-full"
      autoplay
      playsinline
    ></video>
    <canvas bind:this={canvasElement} class="hidden"></canvas>

    <!-- Realtime overlay (document mode) -->
    {#if isDocumentMode}
      <canvas
        bind:this={overlayCanvas}
        class="realtime-overlay absolute z-5 pointer-events-none"
      ></canvas>
    {:else}
      <!-- Static scanner frame (simple mode) -->
      <div class="scanner-overlay absolute inset-0 z-5 pointer-events-none">
        <div class="scanner-frame">
          <div class="corner-marker tl"></div>
          <div class="corner-marker tr"></div>
          <div class="corner-marker bl"></div>
          <div class="corner-marker br"></div>
        </div>
      </div>
    {/if}

    <!-- Guide text -->
    <div class="scanner-guide-top">
      {isDocumentMode ? t('camera.guideDocument') : t('camera.guideSimple')}
    </div>

    <!-- Free tier banner -->
    {#if isFreeUser}
      <button class="free-tier-banner" onclick={showUpgradeHint}>
        <span class="free-tier-label">Free</span>
        <span class="free-tier-remaining">{remaining}/{$subscription.dailyUploadLimit}</span>
      </button>
    {/if}
    <div class="scanner-brand">ScanToDesk</div>

    <!-- Detection status (document mode) -->
    {#if isDocumentMode}
      <RealtimeOverlay state={detectionState} />
    {/if}

    <!-- Recovering overlay (blocks camera during health check after tab return or reconnect) -->
    {#if $isRecovering || $connectionStatus === 'reconnecting'}
      <div class="recovering-overlay absolute inset-0 z-30 flex flex-col items-center justify-center">
        <div class="recovering-content flex flex-col items-center gap-3">
          <svg class="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-white text-lg font-medium">{t('camera.recovering')}</span>
          <span class="text-white/70 text-sm">{t('camera.recoveringHint')}</span>
        </div>
      </div>
    {/if}

    <!-- Upgrade hint toast -->
    {#if showUpgradeToast}
      <div class="upgrade-toast absolute top-14 left-4 right-4 z-30 px-4 py-3 rounded-lg text-center">
        <p class="text-sm font-medium whitespace-pre-line">{t('upload.upgradeHint')}</p>
      </div>
    {/if}

    <!-- Capture flash -->
    <div id="capture-flash" class="hidden"></div>
  </main>

  <!-- Footer controls -->
  <footer class="absolute bottom-0 left-0 right-0 z-10 flex flex-col items-center safe-area-bottom" style="padding: 12px 24px 24px;">
    <ModeToggle
      mode={$scanMode}
      disabled={$scannedPages.length > 0 && $scanMode === 'document'}
      onChange={handleModeChange}
    />
    <CaptureButton
      onclick={handleManualCapture}
      disabled={!cameraReady || capturing || $isRecovering}
    />
  </footer>
</div>

<style>
  .screen {
    display: flex;
  }

  .status-bar {
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
  }

  .queue-status {
    background: rgba(234, 179, 8, 0.9);
    color: #1f2937;
  }

  .camera-video {
    object-fit: contain;
    background: #000;
  }

  .scanner-overlay {
    position: absolute;
    inset: 0;
  }

  .scanner-frame {
    position: absolute;
    top: 4%;
    left: 15%;
    right: 15%;
    bottom: 4%;
  }

  .corner-marker {
    position: absolute;
    width: 28px;
    height: 28px;
    border-color: white;
    border-style: solid;
    border-width: 0;
  }
  .corner-marker.tl { top: 0; left: 0; border-top-width: 3px; border-left-width: 3px; border-top-left-radius: 8px; }
  .corner-marker.tr { top: 0; right: 0; border-top-width: 3px; border-right-width: 3px; border-top-right-radius: 8px; }
  .corner-marker.bl { bottom: 0; left: 0; border-bottom-width: 3px; border-left-width: 3px; border-bottom-left-radius: 8px; }
  .corner-marker.br { bottom: 0; right: 0; border-bottom-width: 3px; border-right-width: 3px; border-bottom-right-radius: 8px; }

  .scanner-guide-top {
    position: absolute;
    top: calc(12% - 36px);
    left: 0;
    right: 0;
    text-align: center;
    color: rgba(255,255,255,0.8);
    font-size: 0.875rem;
    font-weight: 500;
    z-index: 6;
    pointer-events: none;
  }

  .scanner-brand {
    position: absolute;
    bottom: calc(22% - 36px);
    left: 0;
    right: 0;
    text-align: center;
    color: rgba(255,255,255,0.4);
    font-size: 0.75rem;
    letter-spacing: 0.1em;
    z-index: 6;
    pointer-events: none;
  }

  .safe-area-bottom {
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }

  .free-tier-banner {
    position: absolute;
    top: calc(12% - 4px);
    left: 50%;
    transform: translateX(-50%);
    z-index: 6;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 9999px;
    background: rgba(234, 179, 8, 0.85);
    color: #1f2937;
    font-size: 0.8rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    line-height: 1.4;
    backdrop-filter: blur(4px);
  }

  .free-tier-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    opacity: 0.8;
  }

  .free-tier-remaining {
    font-size: 0.85rem;
    font-weight: 700;
  }

  .upgrade-toast {
    background: rgba(234, 179, 8, 0.95);
    color: #1f2937;
    animation: toast-in 0.2s ease-out;
  }

  @keyframes toast-in {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .recovering-overlay {
    background: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(4px);
  }

  #capture-flash {
    position: absolute;
    inset: 0;
    background: white;
    z-index: 15;
    opacity: 0;
    pointer-events: none;
  }

  :global(.capture-flash) {
    animation: flash 0.15s ease-out;
  }

  @keyframes flash {
    0% { opacity: 0.8; }
    100% { opacity: 0; }
  }
</style>
